import { Crown, Pencil, Plus, Trash2, UserMinus, UserPlus } from 'lucide-react';
import * as React from 'react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Dialog,
  DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
  Tooltip, TooltipContent, TooltipTrigger,
} from '@/components/cargofl-ui';
import { ApiError, toastFailure } from '@/lib/api';
import { ConfirmButton } from '@/components/confirm-dialog';
import {
  useCreateTeam, useDeleteTeam, useRenameTeam, useSetMembership,
} from '@/lib/admin-api';
import { usePeople, useTeams, type TeamRow } from '@/lib/people-api';

/**
 * Teams and who leads them.
 *
 * Leadership is not decoration: a team lead approves weeks and leave for everybody
 * in the team, so the crown here is an authorisation change. That is why it is
 * shown as a distinct badge rather than a checkbox buried in a row.
 */
export function TeamsPanel() {
  const { data: teams, isPending } = useTeams();
  const { data: people } = usePeople(false);
  const createTeam = useCreateTeam();
  const deleteTeam = useDeleteTeam();

  const [naming, setNaming] = React.useState<TeamRow | null | undefined>(undefined);
  const [managing, setManaging] = React.useState<TeamRow | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  if (isPending) return <Skeleton className="h-64 w-full" />;
  const rows = teams ?? [];

  const remove = (team: TeamRow) => {
    setError(null);
    setNotice(null);
    deleteTeam.mutate(team.id, {
      onSuccess: () =>
        // Said plainly, because the memberships going with it changes who can
        // approve for those people.
        setNotice(
          `Deleted "${team.name}". ${team.memberCount} membership(s) went with it — those people now rely on their supervisor for approvals.`,
        ),
      onError: (e) => {
        setError(e instanceof ApiError ? e.message : 'Could not delete that team.');
        toastFailure(`Could not delete "${team.name}"`)(e);
      },
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="ts-num text-sm text-muted-foreground">{rows.length} teams</span>
        <Button hint={"Create a team — its lead approves the members' weeks"} onClick={() => setNaming(null)}>
          <Plus className="size-4" />
          Add a team
        </Button>
      </div>

      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <Alert>
          <AlertDescription className="text-sm leading-relaxed">{notice}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[620px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Team</TableHead>
                  <TableHead className="text-right">Members</TableHead>
                  <TableHead>Leads</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((team) => (
                  <TableRow key={team.id}>
                    <TableCell className="font-medium">{team.name}</TableCell>
                    <TableCell className="ts-num text-right">{team.memberCount}</TableCell>
                    <TableCell>
                      {team.leads.length === 0 ? (
                        <span className="text-sm text-muted-foreground">none</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {team.leads.map((l) => (
                            <Badge key={l.id} variant="secondary">{l.displayName}</Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button hint={"Add or remove people, and choose the lead"} variant="ghost" size="sm" onClick={() => setManaging(team)}>
                          <UserPlus className="size-4" />
                          Members
                        </Button>
                        <Button variant="ghost" size="icon" aria-label="Rename team"
                          onClick={() => setNaming(team)}>
                          <Pencil className="size-4" />
                        </Button>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <ConfirmButton
                              variant="ghost"
                              size="icon" hint={false}
                              aria-label="Delete team"
                              disabled={deleteTeam.isPending}
                              pending={deleteTeam.isPending}
                              title={`Delete the team "${team.name}"?`}
                              description={
                                <>
                                  {/* The membership count is the consequence worth
                                      stating: it changes who can approve for whom. */}
                                  {team.memberCount} membership
                                  {team.memberCount === 1 ? '' : 's'} go with it, so those
                                  people will rely on their own supervisor for approvals
                                  instead. Everyone&rsquo;s recorded hours are untouched.
                                </>
                              }
                              confirmLabel="Delete team"
                              destructive
                              onConfirm={() => remove(team)}
                            >
                              <Trash2 className="size-4" />
                            </ConfirmButton>
                          </TooltipTrigger>
                          <TooltipContent>
                            Deleting a team keeps everyone&rsquo;s hours &mdash; only the
                            memberships go
                          </TooltipContent>
                        </Tooltip>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {naming !== undefined && (
        <NameDialog
          team={naming}
          onClose={() => setNaming(undefined)}
          onCreate={(name) => createTeam.mutateAsync(name)}
        />
      )}
      {managing && (
        <MembersDialog
          team={managing}
          people={people ?? []}
          onClose={() => setManaging(null)}
        />
      )}
    </div>
  );
}

function NameDialog({
  team,
  onClose,
  onCreate,
}: {
  team: TeamRow | null;
  onClose: () => void;
  onCreate: (name: string) => Promise<unknown>;
}) {
  const rename = useRenameTeam();
  const [name, setName] = React.useState(team?.name ?? '');
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (team) await rename.mutateAsync({ id: team.id, name });
      else await onCreate(name);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that name.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{team ? 'Rename team' : 'Add a team'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="team-name">Name</Label>
            <Input id="team-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({
  team,
  people,
  onClose,
}: {
  team: TeamRow;
  people: { id: string; displayName: string; jobTitle: string | null }[];
  onClose: () => void;
}) {
  const setMembership = useSetMembership();
  const [error, setError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState<string | null>(null);

  // The list refetches after each change, so this reads from the freshest copy
  // rather than holding its own state that could drift from it.
  const { data: teams } = useTeams();
  const current = teams?.find((t) => t.id === team.id) ?? team;
  const members = current.members ?? [];
  const memberIds = new Set(members.map((m) => m.id));

  const act = (args: { employeeId: string; member: boolean; isLead?: boolean }) => {
    setError(null);
    setMembership.mutate(
      { teamId: team.id, ...args },
      { onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not change that.') },
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{current.name} &mdash; members</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Add somebody</Label>
            <Combobox
              value={adding}
              onChange={(id) => {
                act({ employeeId: id, member: true });
                setAdding(null);
              }}
              options={people
                .filter((p) => !memberIds.has(p.id))
                .map((p) => ({ value: p.id, label: p.displayName, description: p.jobTitle ?? undefined }))}
              placeholder="Search for a person"
              searchPlaceholder="Search people"
            />
          </div>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}

          <div className="max-h-72 space-y-1 overflow-y-auto">
            {members.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                Nobody is in this team yet.
              </p>
            )}
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{member.displayName}</div>
                  {member.isLead && (
                    <div className="text-xs text-muted-foreground">
                      approves for this team
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant={member.isLead ? 'secondary' : 'ghost'}
                        size="icon" hint={false}
                        aria-label={member.isLead ? 'Remove as lead' : 'Make lead'}
                        onClick={() =>
                          act({ employeeId: member.id, member: true, isLead: !member.isLead })
                        }
                      >
                        <Crown className="size-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {member.isLead
                        ? 'Stop them approving for this team'
                        : 'Let them approve weeks and leave for this team'}
                    </TooltipContent>
                  </Tooltip>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Remove from team"
                    onClick={() => act({ employeeId: member.id, member: false })}
                  >
                    <UserMinus className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
